import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { type ApiCatalogueEntry, apiCatalogueEntrySchema } from '../schemas/workflowSchemas';
import { workflowKeys } from './workflowKeys';

// Business display / fallback-priority order per the SEBI spec (owner's document),
// keyed by catalogue operationId — lower runs first. The backend returns entries
// sorted alphabetically by OperationId (e.g. PAN → Lite, OCR, Verify), which is
// NOT the intended sequence; we re-order here so every consumer (Method-tab
// priority default, step picker, phone preview) follows the document order:
//   PAN:     Verification → Lite → OCR
//   Aadhaar: DigiLocker (without PIN → with PIN) → Secure QR → OCR → OVSE
// Operations not listed keep their server order after the ranked ones.
const OPERATION_ORDER: Record<string, number> = {
  Post_Pan_Verify: 0,
  Post_Pan_Lite: 1,
  Post_Pan_Ocr: 2,
  Post_Aadhaar_Digilocker_Pinless: 0,
  Post_Aadhaar_Digilocker_Pin: 1,
  Post_Aadhaar_SecureQr: 2,
  Post_Aadhaar_Ocr: 3,
  Post_Aadhaar_Ovse: 4,
};

function rank(operationId: string): number {
  return OPERATION_ORDER[operationId] ?? 999;
}

// Stable sort that preserves the backend's category → tag grouping and only
// re-orders APIs WITHIN a tag by the document sequence above.
function orderByDocumentSequence(entries: ApiCatalogueEntry[]): ApiCatalogueEntry[] {
  return entries
    .slice()
    .sort(
      (a, b) =>
        a.category.localeCompare(b.category) ||
        a.tag.localeCompare(b.tag) ||
        rank(a.operationId) - rank(b.operationId),
    );
}

export function useApiCatalogue(tag?: string) {
  const tenantId = useTenantStore((s) => s.tenantId);

  return useQuery({
    queryKey: workflowKeys.catalogueByTag(tenantId ?? '', tag ?? ''),
    queryFn: async ({ signal }) => {
      const searchParams: Record<string, string> = {};
      if (tag) searchParams.tag = tag;
      const raw = await apiClient
        .get(`api/v1/t/${tenantId}/workflows/catalogue`, { searchParams, signal })
        .json();
      return orderByDocumentSequence(apiCatalogueEntrySchema.array().parse(raw));
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}
